/**
 * /settings/agent-identities/:id — détail agent + actions Disable/Enable (9.M.7).
 *
 * Surface super_admin only. Pattern parité BrokerDetailPage 9.P.3.C : sticky
 * header + breadcrumb + 2-col layout + status badge + actions block.
 *
 * Actions Phase 1 :
 *   - Disable (soft, réversible) → confirm dialog
 *   - Enable (re-activer) → confirm dialog
 *   - PAS d'edit display_name/description/avatar (immutable Phase 1 — dette future)
 *   - PAS de DELETE (RLS refuse — soft-disable only)
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useParams } from "react-router-dom";

import { useAuth } from "../../../contexts/AuthContext";
import { ApiException } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import { useAgentIdentity } from "../../../lib/queries/agentIdentities";

import { DisableAgentDialog } from "./_components/DisableAgentDialog";
import { EnableAgentDialog } from "./_components/EnableAgentDialog";

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  return iso.slice(0, 19).replace("T", " ");
}

export function AgentIdentityDetailPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const { data: agent, isLoading, error, refetch } = useAgentIdentity(id);
  const [disableOpen, setDisableOpen] = useState(false);
  const [enableOpen, setEnableOpen] = useState(false);

  if (user && user.role !== "super_admin") {
    return <Navigate to="/" replace />;
  }

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-screen-xl">
        <div className="h-32 rounded-xl bg-slate-100 motion-safe:animate-pulse motion-reduce:animate-none" />
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div className="mx-auto w-full max-w-screen-xl">
        <div
          role="alert"
          className="flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          <span>
            {error instanceof ApiException
              ? getApiErrorMessage(error, lang) || t("errors.generic")
              : t("agent_identities.detail.not_found")}
          </span>
          <button
            type="button"
            onClick={() => void refetch()}
            className="rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
          >
            {t("common.retry")}
          </button>
        </div>
      </div>
    );
  }

  const isDisabled = !!agent.disabled_at;

  return (
    <div className="mx-auto w-full max-w-screen-xl">
      <header className="sticky top-16 z-10 -mx-4 border-b border-slate-200 bg-slate-50/95 px-4 py-4 backdrop-blur-sm lg:-mx-8 lg:top-0 lg:px-8 lg:py-5">
        <nav aria-label={t("common.breadcrumb")} className="text-xs text-slate-500">
          <Link
            to="/settings/agent-identities"
            className="rounded-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
          >
            {t("agent_identities.list.title")}
          </Link>
          <span aria-hidden="true" className="mx-1.5">
            ›
          </span>
          <span className="text-slate-700">{agent.display_name}</span>
        </nav>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              {agent.display_name}
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              {t(`agent_identities.kinds.${agent.kind}`, { defaultValue: agent.kind })}
              <span aria-hidden="true" className="text-slate-300"> · </span>
              {agent.tenant_id === null
                ? t("agent_identities.scope.global")
                : t("agent_identities.scope.tenant")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isDisabled ? (
              <button
                type="button"
                onClick={() => setEnableOpen(true)}
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 touch-manipulation"
              >
                {t("agent_identities.detail.enable_cta")}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setDisableOpen(true)}
                className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 touch-manipulation"
              >
                {t("agent_identities.detail.disable_cta")}
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="grid gap-5 py-6 lg:grid-cols-3 lg:py-8">
        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2">
          <h2 className="text-base font-semibold text-slate-900">
            {t("agent_identities.detail.section_info")}
          </h2>
          <dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-4 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-xs font-medium text-slate-500">
                {t("agent_identities.detail.field_display_name")}
              </dt>
              <dd className="mt-1 text-slate-900">{agent.display_name}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-500">
                {t("agent_identities.detail.field_kind")}
              </dt>
              <dd className="mt-1 text-slate-900">
                {t(`agent_identities.kinds.${agent.kind}`, { defaultValue: agent.kind })}
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-xs font-medium text-slate-500">
                {t("agent_identities.detail.field_description")}
              </dt>
              <dd className="mt-1 whitespace-pre-line text-slate-900">
                {agent.description ?? <span className="text-slate-400">—</span>}
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-xs font-medium text-slate-500">
                {t("agent_identities.detail.field_avatar_url")}
              </dt>
              <dd className="mt-1 break-all text-slate-900">
                {agent.avatar_url ?? <span className="text-slate-400">—</span>}
              </dd>
            </div>
          </dl>
        </section>

        <aside className="space-y-5">
          <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-base font-semibold text-slate-900">
              {t("agent_identities.detail.section_status")}
            </h2>
            <dl className="mt-4 space-y-3 text-sm">
              <div>
                <dt className="text-xs font-medium text-slate-500">
                  {t("agent_identities.detail.field_status")}
                </dt>
                <dd className="mt-1">
                  {isDisabled ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700 ring-1 ring-red-200">
                      <span
                        aria-hidden="true"
                        className="h-1.5 w-1.5 rounded-full bg-red-500"
                      />
                      {t("agent_identities.status.disabled")}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-emerald-200">
                      <span
                        aria-hidden="true"
                        className="h-1.5 w-1.5 rounded-full bg-emerald-500"
                      />
                      {t("agent_identities.status.active")}
                    </span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-slate-500">
                  {t("agent_identities.detail.field_scope")}
                </dt>
                <dd className="mt-1 text-slate-900">
                  {agent.tenant_id === null
                    ? t("agent_identities.scope.global")
                    : t("agent_identities.scope.tenant")}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-slate-500">
                  {t("agent_identities.detail.field_created_at")}
                </dt>
                <dd className="mt-1 text-slate-900">{fmtDateTime(agent.created_at)}</dd>
              </div>
              {isDisabled && (
                <div>
                  <dt className="text-xs font-medium text-slate-500">
                    {t("agent_identities.detail.field_disabled_at")}
                  </dt>
                  <dd className="mt-1 text-slate-900">{fmtDateTime(agent.disabled_at)}</dd>
                </div>
              )}
            </dl>
          </section>
        </aside>
      </div>

      {disableOpen && (
        <DisableAgentDialog
          agentId={agent.id}
          agentDisplayName={agent.display_name}
          onClose={() => setDisableOpen(false)}
        />
      )}
      {enableOpen && (
        <EnableAgentDialog
          agentId={agent.id}
          agentDisplayName={agent.display_name}
          onClose={() => setEnableOpen(false)}
        />
      )}
    </div>
  );
}
